import { useState, useEffect, useRef, useMemo } from 'react'
import axios from 'axios'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { extractErrorMessage } from '../utils/apitools'
import useAccount from './useAccount'
import useSnackbar from './useSnackbar'
import useLoading from './useLoading'
import { API_BASE_URL } from '../constants/system'
import {
  SubmitBatchesRequest,
  SubmitBatchesResult,
  AutoCoderPipeline,
  AutoCoderImportConfig,
  AutoCoderPipelineRecord,
  AutoCoderFramework,
  AutoCoderFrameworkSuggestion,
  PipelineLifecycle,
} from '../types/gotypes'

interface AutoCoderProgressResponse {
  import_id: string
  state: string
  total_records: number
  pending: number
  queued: number
  running: number
  completed: number
  failed: number
  percent_done: number
  frameworks: AutoCoderFramework[]
  accumulated_suggestions: AutoCoderFrameworkSuggestion[]
}

// Response of POST /pipelines/:id/flags — the lifecycle plus the three
// EFFECTIVE switches, already derived server-side.
//
// coding is the DRAIN gate: may records that entered a pipeline be coded?
// There used to be a second gate here, batching (the SEAL gate — may new work
// enter the queue lanes at all?). T19 (2026-08-12) retired it: entering a
// pipeline and being batched are now one atomic write, so there is no gated
// moment between them.
export interface PipelineFlagsResponse {
  id: string
  lifecycle: PipelineLifecycle
  connector_enabled: boolean
  coding_enabled: boolean
}

// Response of PUT /pipelines/:id/billing: the team the pipeline's work is
// billed to now, and the address its batches are attributed to.
export interface PipelineBillingResponse {
  id: string
  billing_team_id: string
  billing_team_name: string
  attributed_email: string
}

interface ListAutoCoderRecordsResponse {
  records: AutoCoderPipelineRecord[]
  total_count: number
  limit: number
  offset: number
}

interface UseAutoCoderOptions {
  customer: string
  job: string
  labels?: Record<string, string>  // Filter pipelines by labels
  pipelineId?: string              // For single pipeline/progress operations
  page?: number          // 0-based page for the list
  rowsPerPage?: number   // page size for the list
  lifecycle?: string     // "long-lived" | "one-shot" | "any" | concrete value
}

export const useAutoCoder = (options: UseAutoCoderOptions) => {
  const { customer, job, labels, pipelineId, page = 0, rowsPerPage = 1000, lifecycle } = options

  const account = useAccount()
  const snackbar = useSnackbar()
  const loading = useLoading()
  const queryClient = useQueryClient()

  const [recordsPage, setRecordsPage] = useState(0)
  const [recordsPerPage, setRecordsPerPage] = useState(100)
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const prevDataStatusRef = useRef<string | undefined>(undefined)

  // Build labels query param string: "key1:value1,key2:value2"
  const labelsParam = useMemo(() => {
    if (!labels || Object.keys(labels).length === 0) return undefined
    return Object.entries(labels).map(([k, v]) => `${k}:${v}`).join(',')
  }, [labels])

  // Query keys
  const listQueryKey = ['autocoder_pipelines', { customer, job, labels: labelsParam, page, rowsPerPage, lifecycle }]
  const pipelineQueryKey = ['autocoder_pipeline', { customer, job, pipelineId }]

  // ============ Pipeline List Operations ============

  // List pipelines with optional label filter
  const pipelinesQuery = useQuery({
    queryKey: listQueryKey,
    enabled: Boolean(customer && job && account.user),
    refetchInterval: 5000, // Poll every 5 seconds to keep statuses fresh
    queryFn: async (): Promise<{ items: AutoCoderPipeline[]; total: number }> => {
      if (!customer || !job || !account.user) return { items: [], total: 0 }
      try {
        const res = await axios.get<{ items: AutoCoderPipeline[]; total: number; limit: number; offset: number }>(
          `${API_BASE_URL}/autocoder/${customer}/${job}/pipelines`,
          { params: { labels: labelsParam, limit: rowsPerPage, offset: page * rowsPerPage, lifecycle } },
        )
        return { items: res.data.items ?? [], total: res.data.total ?? 0 }
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
  })

  // Get single pipeline by ID
  const pipelineQuery = useQuery({
    queryKey: pipelineQueryKey,
    enabled: Boolean(customer && job && pipelineId && pipelineId !== 'new' && account.user),
    // Poll every 3 seconds while pipeline is in importing or processing state to catch status changes
    refetchInterval: (query) => {
      const pipeline = query.state.data
      const isImporting = pipeline?.data_status === 'importing' ||
        pipeline?.data_status === 'scraping' ||
        pipeline?.data_status === 'loading'
      const isProcessing = pipeline?.coding_status === 'processing'
      return (isImporting || isProcessing) ? 3000 : false
    },
    queryFn: async () => {
      if (!customer || !job || !pipelineId || pipelineId === 'new' || !account.user) return null
      try {
        const res = await axios.get<AutoCoderPipeline>(
          `${API_BASE_URL}/autocoder/${customer}/${job}/pipelines/${pipelineId}`
        )
        return res.data
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
  })

  // Detect when data_status transitions to 'ready' and invalidate records cache
  // This handles Apify imports where completion happens via the backend control loop
  const currentDataStatus = pipelineQuery.data?.data_status
  useEffect(() => {
    const wasImporting = prevDataStatusRef.current === 'importing' ||
      prevDataStatusRef.current === 'scraping' ||
      prevDataStatusRef.current === 'loading'
    const isNowReady = currentDataStatus === 'ready'

    if (wasImporting && isNowReady) {
      // Import just completed - invalidate records to trigger fresh fetch
      queryClient.invalidateQueries({ queryKey: ['autocoder_records'] })
    }

    prevDataStatusRef.current = currentDataStatus
  }, [currentDataStatus, queryClient])

  // Selected pipeline from list
  const selectedPipeline = useMemo(() => {
    if (!pipelineId || pipelineId === 'new') return undefined
    return pipelinesQuery.data?.items?.find(p => p.id === pipelineId)
  }, [pipelineId, pipelinesQuery.data])

  // ============ Pipeline CRUD Mutations ============

  // Create pipeline
  const createPipelineMutation = useMutation({
    mutationFn: async (payload: {
      config: AutoCoderImportConfig
      labels?: Record<string, string>
      system_name?: string
      lifecycle?: PipelineLifecycle
    }) => {
      loading.setLoading(true)
      const res = await axios.post<AutoCoderPipeline>(
        `${API_BASE_URL}/autocoder/${customer}/${job}/pipelines`,
        payload,
      )
      return res.data
    },
    onSuccess: () => {
      loading.setLoading(false)
      queryClient.invalidateQueries({ queryKey: listQueryKey })
    },
    onError: (error) => {
      loading.setLoading(false)
      snackbar.error(extractErrorMessage(error))
    },
  })

  // Update pipeline
  const updatePipelineMutation = useMutation({
    mutationFn: async (payload: { id: string; config: AutoCoderImportConfig; labels?: Record<string, string> }) => {
      loading.setLoading(true)
      const res = await axios.put<AutoCoderPipeline>(
        `${API_BASE_URL}/autocoder/${customer}/${job}/pipelines/${payload.id}`,
        { config: payload.config, labels: payload.labels }
      )
      return res.data
    },
    onSuccess: () => {
      loading.setLoading(false)
      queryClient.invalidateQueries({ queryKey: listQueryKey })
      queryClient.invalidateQueries({ queryKey: pipelineQueryKey })
    },
    onError: (error) => {
      loading.setLoading(false)
      snackbar.error(extractErrorMessage(error))
    },
  })

  // Delete pipeline
  const deletePipelineMutation = useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`${API_BASE_URL}/autocoder/${customer}/${job}/pipelines/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listQueryKey })
      snackbar.success('Pipeline deleted successfully')
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  // Start pipeline processing
  const startPipelineMutation = useMutation({
    mutationFn: async (id: string) => {
      loading.setLoading(true)
      loading.setProgress('Starting pipeline', 0, 0)
      await axios.post(`${API_BASE_URL}/autocoder/${customer}/${job}/pipelines/${id}/start`)
    },
    onSuccess: () => {
      loading.setLoading(false)
      loading.setProgress('', 0, 0)
      queryClient.invalidateQueries({ queryKey: listQueryKey })
      queryClient.invalidateQueries({ queryKey: pipelineQueryKey })
      snackbar.success('Pipeline started successfully')
    },
    onError: (error) => {
      loading.setLoading(false)
      loading.setProgress('', 0, 0)
      snackbar.error(extractErrorMessage(error))
    },
  })

  // Reset pipeline (clear records and set back to created state)
  const resetPipelineMutation = useMutation({
    mutationFn: async (id: string) => {
      loading.setLoading(true)
      await axios.post(`${API_BASE_URL}/autocoder/${customer}/${job}/pipelines/${id}/reset`)
    },
    onSuccess: () => {
      loading.setLoading(false)
      queryClient.invalidateQueries({ queryKey: listQueryKey })
      queryClient.invalidateQueries({ queryKey: pipelineQueryKey })
      snackbar.success('Pipeline reset successfully')
    },
    onError: (error) => {
      loading.setLoading(false)
      snackbar.error(extractErrorMessage(error))
    },
  })

  // Retry failed records
  const retryFailedMutation = useMutation({
    mutationFn: async (id: string) => {
      loading.setLoading(true)
      // `batches_enqueued` never existed on this response — the server has
      // always sent `batches_created`. The two "nothing happened" cases
      // (no failures at all; every failure still owned by a live batch) are
      // 200s now rather than 400s, so the server's own message is what to
      // show: "Retrying 0 failed records" would be a lie in both.
      const res = await axios.post<{
        message: string
        records_retried: number
        records_in_flight: number
        failed_records: number
        batches_created: number
      }>(`${API_BASE_URL}/autocoder/${customer}/${job}/pipelines/${id}/retry-failed`)
      return res.data
    },
    onSuccess: (data) => {
      loading.setLoading(false)
      queryClient.invalidateQueries({ queryKey: listQueryKey })
      queryClient.invalidateQueries({ queryKey: pipelineQueryKey })
      queryClient.invalidateQueries({ queryKey: ['autocoder_records'] })
      queryClient.invalidateQueries({ queryKey: ['autocoder_progress'] })
      queryClient.invalidateQueries({ queryKey: ['autocoder_failed_records'] })
      if (data.records_retried > 0) {
        snackbar.success(data.message)
      } else {
        snackbar.info(data.message)
      }
    },
    onError: (error) => {
      loading.setLoading(false)
      snackbar.error(extractErrorMessage(error))
    },
  })

  // Create records for a pipeline (called when user confirms data source)
  const createRecordsMutation = useMutation({
    mutationFn: async (payload: { id: string; rows: Array<{ id: string; source_text: string }> }) => {
      loading.setLoading(true)
      const res = await axios.post<{ record_count: number }>(
        `${API_BASE_URL}/autocoder/${customer}/${job}/pipelines/${payload.id}/records`,
        { rows: payload.rows }
      )
      return res.data
    },
    onSuccess: () => {
      loading.setLoading(false)
      queryClient.invalidateQueries({ queryKey: listQueryKey })
      queryClient.invalidateQueries({ queryKey: pipelineQueryKey })
      queryClient.invalidateQueries({ queryKey: ['autocoder_records', { customer, job, pipelineId }] })
    },
    onError: (error) => {
      loading.setLoading(false)
      snackbar.error(extractErrorMessage(error))
    },
  })

  // Set one or both of the independent pipeline switches (C11). Omitted fields
  // leave that switch untouched server-side, so a single toggle never has to
  // know the other's value. The response carries the EFFECTIVE flags, but we
  // still invalidate so the chips render server truth rather than a guess.
  const setPipelineFlagsMutation = useMutation({
    mutationFn: async (payload: {
      id: string
      connector_enabled?: boolean
      coding_enabled?: boolean
    }) => {
      const body: {
        connector_enabled?: boolean
        coding_enabled?: boolean
      } = {}
      if (payload.connector_enabled !== undefined) body.connector_enabled = payload.connector_enabled
      if (payload.coding_enabled !== undefined) body.coding_enabled = payload.coding_enabled
      const res = await axios.post<PipelineFlagsResponse>(
        `${API_BASE_URL}/autocoder/${customer}/${job}/pipelines/${payload.id}/flags`,
        body,
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listQueryKey })
      queryClient.invalidateQueries({ queryKey: pipelineQueryKey })
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  // Set the team the pipeline's work (and its connector's) is billed to; ""
  // clears it. Applied on confirm, like the coding gate, never through the
  // draft save bar: the server writes the one key, in any pipeline state.
  const setPipelineBillingMutation = useMutation({
    mutationFn: async (payload: { id: string; teamId: string }) => {
      const res = await axios.put<PipelineBillingResponse>(
        `${API_BASE_URL}/autocoder/${customer}/${job}/pipelines/${payload.id}/billing`,
        { team_id: payload.teamId },
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listQueryKey })
      queryClient.invalidateQueries({ queryKey: pipelineQueryKey })
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  // Pause/activate change the pipeline's LIFECYCLE, which resets the
  // pipeline's OWN gates — batching and coding — to that lifecycle's defaults
  // server-side (ApplyLifecycle). It deliberately does NOT touch
  // `connector_enabled`: importing is a data-collection concern, so an
  // Activate click can never arm the scheduled connector loop. That is a
  // different axis from setPipelineFlags, which flips one gate (including the
  // connector) and leaves the lifecycle alone. The response is the same
  // effective-switches shape and still carries all three.
  const setPipelineLifecycleMutation = useMutation({
    mutationFn: async (payload: { id: string; action: 'pause' | 'activate' }) => {
      const res = await axios.post<PipelineFlagsResponse>(
        `${API_BASE_URL}/autocoder/${customer}/${job}/pipelines/${payload.id}/${payload.action}`,
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listQueryKey })
      queryClient.invalidateQueries({ queryKey: pipelineQueryKey })
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  // ============ Progress Tracking (for active pipeline) ============

  // Poll for progress when we have an active pipelineId
  const progressQuery = useQuery({
    queryKey: ['autocoder_progress', { customer, job, pipelineId }],
    enabled: Boolean(customer && job && account.user && pipelineId && pipelineId !== 'new'),
    refetchInterval: pipelineId ? 2000 : false, // Poll every 2 seconds when active
    queryFn: async () => {
      if (!customer || !job || !account.user || !pipelineId) return null
      try {
        const res = await axios.get<AutoCoderProgressResponse>(
          `${API_BASE_URL}/autocoder/${customer}/${job}/pipelines/${pipelineId}/progress`
        )
        return res.data
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
  })

  // Load records with pagination
  // Poll during processing to show live updates
  const isProcessing = pipelineQuery.data?.coding_status === 'processing'
  const recordsQuery = useQuery({
    queryKey: ['autocoder_records', {
      customer,
      job,
      pipelineId,
      page: recordsPage,
      perPage: recordsPerPage,
    }],
    enabled: Boolean(customer && job && account.user && pipelineId && pipelineId !== 'new'),
    refetchInterval: isProcessing ? 3000 : false, // Poll every 3 seconds while processing
    queryFn: async () => {
      if (!customer || !job || !account.user || !pipelineId) return null
      try {
        const offset = recordsPage * recordsPerPage
        const res = await axios.get<ListAutoCoderRecordsResponse>(
          `${API_BASE_URL}/autocoder/${customer}/${job}/pipelines/${pipelineId}/records`,
          {
            params: {
              limit: recordsPerPage,
              offset,
            },
          }
        )
        return res.data
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
  })

  // Load failed records (only when there are failures)
  const hasFailed = (progressQuery.data?.failed ?? 0) > 0
  const failedRecordsQuery = useQuery({
    queryKey: ['autocoder_failed_records', { customer, job, pipelineId }],
    enabled: Boolean(customer && job && account.user && pipelineId && pipelineId !== 'new' && hasFailed),
    queryFn: async () => {
      if (!customer || !job || !account.user || !pipelineId) return null
      const res = await axios.get<ListAutoCoderRecordsResponse>(
        `${API_BASE_URL}/autocoder/${customer}/${job}/pipelines/${pipelineId}/records`,
        { params: { state: 'failed', limit: 100, offset: 0 } }
      )
      return res.data
    },
  })

  // Clean up polling interval when component unmounts or pipelineId changes
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
    }
  }, [pipelineId])

  // ============ Convenience Methods ============

  const createPipeline = async (
    config: AutoCoderImportConfig,
    pipelineLabels?: Record<string, string>,
    opts?: { systemName?: string; lifecycle?: PipelineLifecycle },
  ) => {
    return createPipelineMutation.mutateAsync({
      config,
      labels: pipelineLabels,
      system_name: opts?.systemName,
      lifecycle: opts?.lifecycle,
    })
  }

  const updatePipeline = async (id: string, config: AutoCoderImportConfig, pipelineLabels?: Record<string, string>) => {
    return updatePipelineMutation.mutateAsync({ id, config, labels: pipelineLabels })
  }

  const deletePipeline = (id: string) => {
    deletePipelineMutation.mutate(id)
  }

  const startPipeline = async (id: string) => {
    await startPipelineMutation.mutateAsync(id)
  }

  const resetPipeline = async (id: string) => {
    await resetPipelineMutation.mutateAsync(id)
  }

  const retryFailed = async (id: string) => {
    await retryFailedMutation.mutateAsync(id)
  }

  const setPipelineFlags = async (
    id: string,
    flags: {
      connector_enabled?: boolean
      coding_enabled?: boolean
    },
  ) => {
    return setPipelineFlagsMutation.mutateAsync({ id, ...flags })
  }

  const setPipelineBilling = async (id: string, teamId: string) => {
    return setPipelineBillingMutation.mutateAsync({ id, teamId })
  }

  const setPipelineLifecycle = async (id: string, action: 'pause' | 'activate') => {
    return setPipelineLifecycleMutation.mutateAsync({ id, action })
  }

  const createRecords = async (id: string, rows: Array<{ id: string; source_text: string }>) => {
    return createRecordsMutation.mutateAsync({ id, rows })
  }

  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: listQueryKey })
    queryClient.invalidateQueries({ queryKey: pipelineQueryKey })
  }

  // Invalidate records cache - useful after data import to ensure fresh data
  const invalidateRecordsCache = () => {
    queryClient.invalidateQueries({ queryKey: ['autocoder_records'] })
  }

  return {
    // Pipeline list
    pipelines: pipelinesQuery.data?.items ?? [],
    pipelinesTotal: pipelinesQuery.data?.total ?? 0,
    pipelinesLoading: pipelinesQuery.isLoading,
    pipelinesError: pipelinesQuery.error,

    // Single pipeline
    pipeline: pipelineQuery.data ?? selectedPipeline,
    pipelineLoading: pipelineQuery.isLoading,
    pipelineError: pipelineQuery.error,
    selectedPipeline,
    isNewPipeline: pipelineId === 'new',

    // CRUD operations
    createPipeline,
    updatePipeline,
    deletePipeline,
    startPipeline,
    resetPipeline,
    retryFailed,
    createRecords,
    setPipelineFlags,
    setPipelineBilling,
    setPipelineLifecycle,

    // Mutation states
    createLoading: createPipelineMutation.isPending,
    updateLoading: updatePipelineMutation.isPending,
    deleteLoading: deletePipelineMutation.isPending,
    startLoading: startPipelineMutation.isPending,
    resetLoading: resetPipelineMutation.isPending,
    retryFailedLoading: retryFailedMutation.isPending,
    createRecordsLoading: createRecordsMutation.isPending,
    setPipelineFlagsLoading: setPipelineFlagsMutation.isPending,
    setPipelineBillingLoading: setPipelineBillingMutation.isPending,
    setPipelineLifecycleLoading: setPipelineLifecycleMutation.isPending,

    // Progress data (legacy compatible)
    progress: progressQuery.data,
    progressIsLoading: progressQuery.isLoading,
    progressIsError: progressQuery.isError,
    progressError: progressQuery.error,

    // Records data (legacy compatible)
    records: recordsQuery.data?.records || [],
    totalRecords: recordsQuery.data?.total_count || 0,
    recordsIsLoading: recordsQuery.isLoading,
    recordsIsError: recordsQuery.isError,
    recordsError: recordsQuery.error,

    // Pagination (legacy compatible)
    recordsPage,
    setRecordsPage,
    recordsPerPage,
    setRecordsPerPage,

    // Failed records
    failedRecords: failedRecordsQuery.data?.records || [],
    failedRecordsCount: failedRecordsQuery.data?.total_count || 0,
    failedRecordsLoading: failedRecordsQuery.isLoading,

    // Accumulated suggestions from progress response (legacy compatible)
    suggestions: progressQuery.data?.accumulated_suggestions || [],

    // Frameworks from progress response (legacy compatible)
    frameworks: progressQuery.data?.frameworks || [],

    // Refresh
    refetch,
    invalidateRecordsCache,
  }
}

export default useAutoCoder

// ─────────────────────────────────────────────────────────────────────────────
// THE SEND (POST /pipelines/:id/submit-batches, T8).
//
// Ingest and sending are two separate acts: a fetch produces a receipt, its
// entities and its records with no pipeline at all, and this is the only thing
// that puts a chosen slice of them into a coding pipeline. It creates no data —
// it selects records that already exist — grouping, packing and opening their
// codings as batches in one transaction on the server.
//
// The endpoint bounds each call to one pass (the server's submitBatchesPassLimit
// records) and reports `remaining` so the caller knows whether to call again.
// That is a CONTRACT, not a hint: a selection bigger than one pass that stops
// after the first call silently sends only part of the selection while
// reporting success. sendToPipeline below owns that loop so no caller has to.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * What is being sent. Exactly one selector is used per send: a whole receipt
 * ("send that fetch") or a set of entities ("send these videos"), which is the
 * entity door the browser uses.
 */
export interface SendToPipelineTarget {
  importBatchId?: string
  entityIds?: string[]
}

export const sendTargetIsEmpty = (target: SendToPipelineTarget | null | undefined): boolean =>
  !target?.importBatchId && !(target?.entityIds && target.entityIds.length > 0)

const zeroSubmitBatchesResult = (): SubmitBatchesResult => ({
  batchesCreated: 0,
  codingsOpened: 0,
  alreadyInFlight: 0,
  batchesDropped: 0,
  remaining: 0,
})

/**
 * Posts one target to the pipeline's submit-batches endpoint, looping while
 * the server reports `remaining > 0`, and reports cumulative totals across
 * every pass through onProgress as they complete (so a caller can show live
 * progress on a selection that spans many passes).
 */
export const sendToPipeline = async (
  customer: string,
  job: string,
  pipelineId: string,
  target: SendToPipelineTarget,
  onProgress?: (totals: SubmitBatchesResult) => void,
): Promise<SubmitBatchesResult> => {
  const body: SubmitBatchesRequest = target.importBatchId
    ? { importBatchId: target.importBatchId }
    : { entity: { entityIds: target.entityIds || [] } }

  const totals = zeroSubmitBatchesResult()
  let remaining = Number.POSITIVE_INFINITY
  while (remaining > 0) {
    let pass: SubmitBatchesResult
    try {
      const res = await axios.post<SubmitBatchesResult>(
        `${API_BASE_URL}/autocoder/${customer}/${job}/pipelines/${pipelineId}/submit-batches`,
        body,
      )
      pass = res.data
    } catch (error) {
      throw new Error(extractErrorMessage(error))
    }
    totals.batchesCreated += pass.batchesCreated
    totals.codingsOpened += pass.codingsOpened
    totals.alreadyInFlight += pass.alreadyInFlight
    totals.batchesDropped += pass.batchesDropped
    remaining = pass.remaining
    totals.remaining = remaining
    onProgress?.({ ...totals })
  }
  return totals
}

export interface UseSendToPipelineResult {
  send: (pipelineId: string, target: SendToPipelineTarget) => Promise<SubmitBatchesResult>
  result: SubmitBatchesResult | undefined
  /** Cumulative totals from the passes completed so far; updates live while sending. */
  progress: SubmitBatchesResult | undefined
  sending: boolean
  error: string | null
  reset: () => void
}

export const useSendToPipeline = (customer: string, job: string): UseSendToPipelineResult => {
  const queryClient = useQueryClient()
  const [progress, setProgress] = useState<SubmitBatchesResult | undefined>(undefined)

  const mutation = useMutation<
    SubmitBatchesResult,
    Error,
    { pipelineId: string; target: SendToPipelineTarget }
  >({
    mutationFn: ({ pipelineId, target }) =>
      sendToPipeline(customer, job, pipelineId, target, setProgress),
    onSuccess: () => {
      // Prefix invalidation throughout: a send changes what every records view
      // shows (records gain a coding) and what the pipeline's queue and batches
      // hold, and none of those readers' page/filter state is knowable here.
      void queryClient.invalidateQueries({ queryKey: ['autocoder', customer, job, 'records'] })
      void queryClient.invalidateQueries({ queryKey: ['autocoder', customer, job, 'batches'] })
      void queryClient.invalidateQueries({ queryKey: ['autocoder-pipeline-queue', customer, job] })
    },
  })

  return {
    send: (pipelineId, target) => mutation.mutateAsync({ pipelineId, target }),
    result: mutation.data,
    progress,
    sending: mutation.isPending,
    error: mutation.error ? mutation.error.message : null,
    reset: () => {
      setProgress(undefined)
      mutation.reset()
    },
  }
}
